import { useState, useEffect, useRef } from 'react';
import './App.css';
import { sounds, initSounds } from './sounds';
import emailjs from 'emailjs-com';

emailjs.init("fNJyQ_YyVCceFyCpQ");
initSounds();

const playSound = (soundName) => {
  if (sounds[soundName]?.src) {
    const audio = new Audio(sounds[soundName].src);
    audio.volume = 0.3;
    audio.play().catch(() => {});
  }
};

const Cursor = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [clicked, setClicked] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isDesktop = window.innerWidth > 768;
      setIsVisible(!isTouchDevice && isDesktop);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);

    const moveCursor = (e) => setPosition({ x: e.clientX, y: e.clientY });
    const handleMouseDown = () => setClicked(true);
    const handleMouseUp = () => setClicked(false);

    if (isVisible) {
      window.addEventListener('mousemove', moveCursor);
      window.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('mousemove', moveCursor);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      className={`pixel-cursor ${clicked ? 'click' : ''}`}
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
    />
  );
};

const Loading = ({ onLoaded }) => {
  useEffect(() => {
    const handleFirstInteraction = async () => {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      await audioContext.resume();
      try {
        sounds.intro.audio.play();
      } catch {}
      onLoaded();
    };

    const handleClick = () => handleFirstInteraction();
    const handleTouch = () => handleFirstInteraction();

    document.addEventListener('click', handleClick, { once: true });
    document.addEventListener('touchstart', handleTouch, { once: true });

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('touchstart', handleTouch);
    };
  }, [onLoaded]);

  return (
    <div className="loading-screen">
      <div className="loading-text pixel-text">
        <span className="desktop-text">CLICK ANYWHERE TO START</span>
        <span className="mobile-text">TAP TO START</span>
      </div>
    </div>
  );
};

const Home = () => {
  const line1 = "Hi! I'm Eva";
  const line2 = "Hajiyeva. Passionate";
  const line3 = "Developer :)";
  const [displayText1, setDisplayText1] = useState([]);
  const [displayText2, setDisplayText2] = useState([]);
  const [displayText3, setDisplayText3] = useState([]);
  const [startTyping, setStartTyping] = useState(false);
  const typingDelay = 100;
  const soundOffset = 25;

  useEffect(() => {
    if (!startTyping) return;

    let currentIndex = 0;
    const timers = [];

    const typeFirstLine = (index) => {
      if (index <= line1.length) {
        if (line1[index - 1] && line1[index - 1] !== ' ') {
          setTimeout(() => playSound('letter'), index * typingDelay - soundOffset);
        }
        setDisplayText1(line1.slice(0, index).split(''));
        currentIndex++;
        const timer = setTimeout(() => typeFirstLine(currentIndex), typingDelay);
        timers.push(timer);
      } else {
        setTimeout(() => typeSecondLine(0), 500);
      }
    };

    const typeSecondLine = (index) => {
      if (index <= line2.length) {
        if (line2[index - 1] && line2[index - 1] !== ' ') {
          setTimeout(
            () => playSound('letter'),
            (line1.length + index) * typingDelay - soundOffset
          );
        }
        setDisplayText2(line2.slice(0, index).split(''));
        currentIndex = index + 1;
        const timer = setTimeout(() => typeSecondLine(currentIndex), typingDelay);
        timers.push(timer);
      } else {
        setTimeout(() => typeThirdLine(0), 500);
      }
    };

    const typeThirdLine = (index) => {
      if (index <= line3.length) {
        if (line3[index - 1] && line3[index - 1] !== ' ') {
          setTimeout(
            () => playSound('letter'),
            (line1.length + line2.length + index) * typingDelay - soundOffset
          );
        }
        setDisplayText3(line3.slice(0, index).split(''));
        currentIndex = index + 1;
        const timer = setTimeout(() => typeThirdLine(currentIndex), typingDelay);
        timers.push(timer);
      }
    };

    typeFirstLine(currentIndex);

    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [startTyping]);

  return (
    <section id="home" className="section">
      {!startTyping && <Loading onLoaded={() => setStartTyping(true)} />}
      <div className="home-content">
        <h1 className="pixel-text home-title">
          <div className="line-container">
            {displayText1.map((char, index) => (
              <span
                key={`line1-${index}`}
                className="pixel-char"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </div>
          <div className="line-container">
            {displayText2.map((char, index) => (
              <span
                key={`line2-${index}`}
                className="pixel-char"
                style={{ animationDelay: `${(line1.length + index) * 0.1 + 0.5}s` }}
              >
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </div>
          <div className="line-container">
            {displayText3.map((char, index) => (
              <span
                key={`line3-${index}`}
                className="pixel-char subtitle"
                style={{
                  animationDelay: `${(line1.length + line2.length + index) * 0.1 + 1.0}s`,
                }}
              >
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </div>
        </h1>
      </div>
      <div className="scanlines"></div>
    </section>
  );
};

const About = () => (
  <section id="about" className="section">
    <div className="pixel-box glitch-box">
      <h2 className="pixel-text section-title">ABOUT ME</h2>
      <div className="terminal">
        <div className="terminal-header">
          <span className="terminal-btn red"></span>
          <span className="terminal-btn yellow"></span>
          <span className="terminal-btn green"></span>
          <span className="terminal-title">user@eva:~</span>
        </div>
        <div className="terminal-body">
          <p>Welcome to my digital realm!</p>
          <p>I'm a university student and a passionate developer with skills in:</p>
          <ul className="pixel-list">
            <li>JavaScript/TypeScript</li>
            <li>React/Node.js</li>
            <li>React Native/Expo</li>
            <li>Web Development</li>
          </ul>
          <p>Currently learning: Game Development</p>
          <br />
          <p>
            I am committed to continuous learning and staying up-to-date with the latest
            advancements in software development. I actively seek opportunities to expand my
            knowledge and gain practical experience in order to enhance my skills and contribute to
            the field.
          </p>
          <p className="blink">_</p>
        </div>
      </div>
    </div>
    <div className="scanlines"></div>
  </section>
);

const Projects = ({ openProjectModal }) => {
  const projectData = [
    {
      id: 1,
      title: 'FARMIUM',
      description:
        'A mobile app that connects plant lovers with their plants. I led the technical development as CTO, building the React Native frontend and coordinating the backend integration.',
      technologies: ['React Native', 'Expo', 'Firebase', 'Node.js'],
      image: 'assets/farmium.png',
      link: 'https://farmiumapp.evagadz.com',
    },

    {
      id: 2,
      title: 'TASK MANAGER',
      description:
        'A productivity application with drag-and-drop functionality for organizing tasks and projects.',
      technologies: ['React', 'Redux', 'Firebase', 'Material UI'],
      image: 'assets/todo.png',
      link: '#',
    },
   
    {
      id: 3,
      title: 'PORTFOLIO V1',
      description:
        'My first portfolio website built with pure HTML/CSS/JS. Featured interactive elements and a clean design.',
      technologies: ['HTML5', 'CSS3', 'JavaScript'],
      image: 'assets/v1.png',
      link: '#',
    },
    {
      id: 4,
      title: 'WEATHER APP',
      description:
        'A responsive weather application that provides real-time weather data and forecasts for locations worldwide.',
      technologies: ['React', 'OpenWeather API', 'CSS3', 'Geolocation API'],
      image: '',
      link: '#',
    },

     {
      id: 5,
      title: 'FARMIUM V2',
      description:
        'Newer and improved version of the first farmium app. Coming soon',
      technologies: ['React Native', 'Expo', 'Firebase', 'Node.js'],
      image: 'assets/farmiumv2.png',
      link: '#',
    },

    
  ];

  return (
    <section id="projects" className="section">
      <div className="pixel-box glitch-box">
        <h2 className="pixel-text section-title">MY PROJECTS</h2>
        <div className="project-grid">
          {projectData.map((project) => (
            <div
              key={project.id}
              className="pixel-card"
              onMouseEnter={() => playSound('hover')}
              onClick={() => openProjectModal(project.id)}
            >
              <div className="pixel-card-header">{project.title}</div>
              <div className="pixel-card-body">
                <p>{project.description.substring(0, 90)}...</p>
                <button
                  className="pixel-button small"
                  onMouseEnter={() => playSound('hover')}
                >
                  VIEW DETAILS
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="scanlines"></div>
    </section>
  );
};

const ProjectModal = ({ isOpen, onClose, projectId }) => {
  const projectData = [
    {
      id: 1,
      title: 'FARMIUM',
      description:
        'Farmium is a mobile app that connects plant lovers with their plants. I led the technical development as CTO, building the React Native frontend and coordinating the backend integration. The app features a user-friendly interface for users to list their plants and take care of them.',
      technologies: ['React Native', 'Expo', 'Firebase', 'Node.js'],
      image: 'assets/farmium.png',
      link: 'https://farmiumapp.evagadz.com',
      features: [
        'Real-time product listings',
        'In-app messaging between farmers and buyers',
        'Secure payment integration',
        'Location-based farm discovery',
      ],
    },
     {
      id: 2,
      title: 'TASK MANAGER',
      description:
        'A productivity application with drag-and-drop functionality for organizing tasks and projects. Users can create boards, lists, and cards to manage their workflow efficiently.',
      technologies: ['React', 'Redux', 'Firebase', 'Material UI'],
      image: 'assets/todo.png',
      link: '#',
      features: [
        'Drag-and-drop functionality',
        'User authentication',
        'Real-time updates',
        'Customizable boards and lists',
      ],
    },
    {
      id: 3,
      title: 'PORTFOLIO V1',
      description:
        'My first portfolio website built with pure HTML/CSS/JS. Featured interactive elements and a clean design to showcase my early projects and skills as a developer.',
      technologies: ['HTML5', 'CSS3', 'JavaScript'],
      image: 'assets/v1.png',
      link: 'https://v1portfolio.evagadz.com',
      features: [
        'Interactive animations',
        'Responsive layout',
        'Project showcase section',
        'Contact form with validation',
      ],
    },
    {
      id: 4,
      title: 'WEATHER APP',
      description:
        'A responsive weather application that provides real-time weather data and forecasts for locations worldwide. Features include current weather conditions, 5-day forecasts, and location-based weather searching.',
      technologies: ['React', 'OpenWeather API', 'CSS3', 'Geolocation API'],
      image: '',
      link: '#',
      features: [
        'Current weather conditions',
        '5-day forecast',
        'Location-based weather',
        'Responsive design for all devices',
      ],
    },

    {
      id: 5,
      title: 'FARMIUM V2',
      description:
        'Farmium is a mobile app that connects plant lovers with their plants. I led the technical development as CTO, building the React Native frontend and coordinating the backend integration. The app features a user-friendly interface for users to list their plants and take care of them.',
      technologies: ['React Native', 'Expo', 'Firebase', 'Node.js'],
      image: 'assets/farmiumv2.png',
      link: '#',
      features: [
        'Real-time product listings',
        'In-app messaging between farmers and buyers',
        'Secure payment integration',
        'Location-based farm discovery',
      ],
    },
   
  ];

  const project = projectData.find((p) => p.id === projectId);

  const scrollYRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      scrollYRef.current = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    } else {
      const y = Math.abs(parseInt(document.body.style.top || '0', 10)) || scrollYRef.current || 0;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, y);
    }

    return () => {
      const y = Math.abs(parseInt(document.body.style.top || '0', 10)) || scrollYRef.current || 0;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, y);
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="pixel-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="pixel-text">{project.title}</h3>
          <button
            className="pixel-button close-btn"
            onClick={onClose}
            onMouseEnter={() => playSound('hover')}
          >
            ✕
          </button>
        </div>
        <div className="modal-content">
          <div className="modal-image-section">
            {project.image ? (
              <div className="modal-image-container">
                <img src={project.image} alt={project.title} className="modal-image" />
              </div>
            ) : (
              <div className="modal-image-placeholder pixel-text">
                <div className="placeholder-text">PROJECT SCREENSHOT</div>
              </div>
            )}
          </div>

          <div className="modal-details">
            <div className="modal-description">
              <h4 className="pixel-text">DESCRIPTION</h4>
              <p>{project.description}</p>
            </div>

            {project.features && project.features.length > 0 && (
              <div className="modal-features">
                <h4 className="pixel-text">FEATURES</h4>
                <ul className="pixel-list">
                  {project.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="modal-tech">
              <h4 className="pixel-text">TECH STACK</h4>
              <div className="tech-tags">
                {project.technologies.map((tech) => (
                  <span key={tech} className="pixel-tag">
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {project.link && project.link !== '#' && (
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="pixel-button project-link"
                onMouseEnter={() => playSound('hover')}
              >
                VISIT PROJECT
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Experience = () => (
  <section id="experience" className="section">
    <div className="pixel-box glitch-box experience-outer-container">
      <h2 className="pixel-text section-title">EXPERIENCE</h2>
      <div className="experience-inner-container">
        <div className="timeline">
          <div className="timeline-item">
            <div className="timeline-year">2024</div>
            <div className="timeline-content">
              <h3>
                <span className="glow-text">Co-founder & CTO</span> @ Farmium
              </h3>
              <p>
                At Farmium, I oversee the company's technical initiatives and guide the direction of
                product development. Collaborating with a dedicated team, we continuously innovate
                and adapt to meet our users' needs.
              </p>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-year">2023</div>
            <div className="timeline-content">
              <h3>
                <span className="glow-text">React Native Developer</span> @ AISTGroup
              </h3>
              <p>
                At AISTGroup, I was actively involved in developing and enhancing a frontend React
                Native application using the Expo framework. My role involved closely collaborating
                with the design team to ensure a seamless user experience across Android and iOS
                platforms.
              </p>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-year">2022</div>
            <div className="timeline-content">
              <h3>
                <span className="glow-text">Website Developer</span> @ Medico.Az
              </h3>
              <p>
                At Medico.az, I contributed to the design and development of an interactive web
                application, streamlining the process for users to order medicines for home
                delivery. Leveraging technologies like PHP, AJAX, jQuery, and MySQLi.
              </p>
            </div>
          </div>

          <div className="timeline-item">
            <h2>Want to download my CV instead? <a href="EvaHajiyevaCV.pdf" download="EvaHajiyevaCV.pdf">Click here</a></h2>
          </div>

        </div>
      </div>
    </div>
    <div className="scanlines"></div>
  </section>
);

const Contact = () => {
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      message: formData.get('message'),
    };

    emailjs
      .send('service_tf4d14n', 'template_dpfol4g', data, 'fNJyQ_YyVCceFyCpQ')
      .then(() => {
        playSound('success');
        setSent(true);
        setIsLoading(false);
        e.target.reset();
        setTimeout(() => setSent(false), 3000);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  return (
    <section id="contact" className="section">
      <div className="pixel-box glitch-box">
        <h2 className="pixel-text section-title">GET IN TOUCH</h2>
        {sent ? (
          <div className="success-message">
            <p className="pixel-text">MESSAGE SENT!</p>
            <div className="pixel-checkmark">✓</div>
          </div>
        ) : (
          <form className="pixel-form" onSubmit={handleSubmit}>
            <input
              type="text"
              name="name"
              className="pixel-input"
              placeholder="NAME"
              onFocus={() => playSound('hover')}
              required
              disabled={isLoading}
            />
            <input
              type="email"
              name="email"
              className="pixel-input"
              placeholder="EMAIL"
              onFocus={() => playSound('hover')}
              required
              disabled={isLoading}
            />
            <textarea
              name="message"
              className="pixel-textarea"
              placeholder="MESSAGE"
              onFocus={() => playSound('hover')}
              required
              disabled={isLoading}
            ></textarea>
            <button
              type="submit"
              className="pixel-button"
              onMouseEnter={() => playSound('hover')}
              disabled={isLoading}
            >
              {isLoading ? 'SENDING...' : 'SEND MESSAGE'}
            </button>
          </form>
        )}
      </div>
      <div className="scanlines"></div>
    </section>
  );
};

const Sidebar = ({ activeSection, setActiveSection }) => {
  const sections = ['home', 'about', 'projects', 'experience', 'contact'];

  return (
    <nav className="sidebar">
      {sections.map((section) => (
        <a
          key={section}
          href={`#${section}`}
          className={`pixel-text nav-link ${activeSection === section ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            playSound('click');
            setActiveSection(section);
            document.getElementById(section).scrollIntoView({ behavior: 'smooth' });
          }}
          onMouseEnter={() => playSound('hover')}
        >
          {section.toUpperCase()}
        </a>
      ))}
    </nav>
  );
};

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const observer = useRef();

  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0.5 }
    );

    document.querySelectorAll('.section').forEach((section) => {
      observer.current.observe(section);
    });

    return () => observer.current.disconnect();
  }, []);

  const openProjectModal = (projectId) => {
    setSelectedProject(projectId);
    setIsModalOpen(true);
    playSound('click');
  };

  const closeProjectModal = () => {
    setIsModalOpen(false);
    playSound('hover');
  };

  return (
    <div className="container">
      <Cursor />
      <div className="noise"></div>
      <Sidebar activeSection={activeSection} setActiveSection={setActiveSection} />
      <Home />
      <About />
      <Projects openProjectModal={openProjectModal} />
      <ProjectModal isOpen={isModalOpen} onClose={closeProjectModal} projectId={selectedProject} />
      <Experience />
      <Contact />
    </div>
  );
}

export default App;
